import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { StorefrontSettingsModel } from "../hooks/useStorefrontSettings";
import { storefrontImageUrl } from "../storefront-appearance";
import type { StorefrontBanner } from "../storefront-banners";
import { storefrontPreviewAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";

export type PreviewProduct = {
	id: number;
	name: string;
	image?: string;
	sellingPrice?: number | string;
	category?: { name?: string };
};
type PreviewBranch = { id: number; name: string; latitude?: number; longitude?: number; deliveryTimeText?: string };
type Catalog = {
	products: Array<{ image?: string | null; [key: string]: unknown }>;
	branches: PreviewBranch[];
	branchId: number | null;
};

/** Render the store application itself. No second set of storefront markup or styles lives in admin. */
export function StorefrontPreview({
	model,
	products = [],
	banners = [],
	hideHeading = false,
}: {
	model: StorefrontSettingsModel;
	products?: PreviewProduct[];
	banners?: StorefrontBanner[];
	hideHeading?: boolean;
}) {
	const { t, i18n } = useTranslation();
	const frame = useRef<HTMLIFrameElement>(null);
	const container = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(340);
	const [viewport, setViewport] = useState(390);
	const [page, setPage] = useState("catalog");
	const [branchId, setBranchId] = useState<number>();
	const [catalog, setCatalog] = useState<Catalog>();
	const [catalogError, setCatalogError] = useState(false);
	const [loading, setLoading] = useState(true);
	const [revision, setRevision] = useState(0);
	const [ready, setReady] = useState(false);
	const retriedStartup = useRef(false);
	const [frameError, setFrameError] = useState(false);
	const ids = JSON.stringify(products.map(p => p.id));
	const url = useMemo(() => {
		const result = new URL("/store-preview/preview.html", window.location.origin);
		result.searchParams.set("parentOrigin", window.location.origin);
		return result;
	}, []);
	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setReady(false);
		setFrameError(false);
		setCatalogError(false);
		storefrontPreviewAPI
			.catalog(JSON.parse(ids), branchId)
			.then(data => {
				if (!cancelled) setCatalog(data);
			})
			.catch(() => {
				if (!cancelled) setCatalogError(true);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [ids, branchId, revision]);
	useEffect(() => {
		if (!container.current) return;
		const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
		observer.observe(container.current);
		return () => observer.disconnect();
	}, []);
	useEffect(() => {
		const listener = (event: MessageEvent) => {
			if (event.source !== frame.current?.contentWindow || event.origin !== url.origin) return;
			if (event.data?.type === "storefront-preview-ready" && event.data.version === 1) {
				setReady(true);
				setFrameError(false);
			}
		};
		window.addEventListener("message", listener);
		return () => window.removeEventListener("message", listener);
	}, [url]);
	useEffect(() => {
		if (ready || loading || catalogError) return;
		// Some browsers leave a newly inserted frame at about:blank during the host's first render.
		// Retry that navigation once; genuine load failures still expose the explicit Retry action.
		const restart = window.setTimeout(() => {
			if (!retriedStartup.current) {
				retriedStartup.current = true;
				setRevision(r => r + 1);
			}
		}, 2000);
		const timer = window.setTimeout(() => setFrameError(true), 12000);
		return () => {
			clearTimeout(restart);
			clearTimeout(timer);
		};
	}, [ready, revision, loading, catalogError, url]);
	const a = model.draft.appearance;
	const c = model.draft.content;
	const profile = model.profile;
	const selectedBranch = catalog?.branches.find(b => b.id === catalog.branchId);
	const payload = useMemo(
		() => ({
			type: "storefront-preview-draft",
			version: 1,
			locale: i18n.language.startsWith("ar") ? "ar" : "en",
			page,
			products: (catalog?.products || []).map(product => ({
				...product,
				image: storefrontImageUrl(product.image || "") || null,
			})),
			branches: catalog?.branches || [],
			branchId: catalog?.branchId ?? null,
			latitude: selectedBranch?.latitude == null ? null : Number(selectedBranch.latitude),
			longitude: selectedBranch?.longitude == null ? null : Number(selectedBranch.longitude),
			resolve: {
				slug: "__draft",
				posId: 0,
				companyId: 0,
				companyName: a.displayName.trim() || profile?.name || t("storefrontDesign.storeName"),
				description: a.tagline.trim() || c.aboutText.trim() || profile?.description || null,
				logo: storefrontImageUrl(a.logoUrl || profile?.logo || "") || null,
				currency: profile?.currency || "SAR",
				contact: {
					phone: c.supportPhone.trim() || profile?.phone || "",
					email: c.supportEmail.trim() || profile?.email || "",
					website: c.supportWebsite.trim() || profile?.website || null,
					whatsapp: c.supportWhatsapp.trim() || null,
					address:
						c.supportAddress.trim() ||
						profile?.address?.trim() ||
						[
							profile?.street,
							profile?.building,
							profile?.district,
							profile?.city,
							profile?.postalCode,
							profile?.country,
						]
							.filter(Boolean)
							.join(", ") ||
						null,
				},
				storefront: {
					...c,
					appearance: {
						...a,
						coverUrl: storefrontImageUrl(a.coverUrl),
						logoUrl: storefrontImageUrl(a.logoUrl),
					},
					banners: banners
						.filter(
							b =>
								b.imageUrl &&
								(!b.startAt || Date.parse(b.startAt) <= Date.now()) &&
								(!b.endAt || Date.parse(b.endAt) >= Date.now()),
						)
						.map(b => ({ ...b, imageUrl: storefrontImageUrl(b.imageUrl) })),
				},
				paymentMethods: model.paymentMethods.filter(m => model.draft.paymentMethodIds.includes(m.id)),
			},
		}),
		[
			a,
			c,
			profile,
			banners,
			catalog,
			selectedBranch,
			model.paymentMethods,
			model.draft.paymentMethodIds,
			i18n.language,
			page,
			t,
		],
	);
	useEffect(() => {
		if (!loading && !catalogError) frame.current?.contentWindow?.postMessage(payload, url.origin);
	}, [payload, ready, loading, catalogError, url]);
	const scale = Math.min(width / viewport, 1);
	const retry = () => setRevision(r => r + 1);
	return (
		<section
			aria-label={t("storefrontDesign.preview")}
			data-testid="storefront-preview"
			data-preview-status={
				catalogError
					? "catalog-error"
					: frameError
						? "frame-error"
						: loading
							? "loading-catalog"
							: ready
								? "ready"
								: "loading-frame"
			}
			className="min-w-0 space-y-3"
		>
			{!hideHeading && (
				<div className="flex flex-wrap justify-between gap-2">
					<h2 className="text-base font-semibold">{t("storefrontDesign.preview")}</h2>
					<span className="text-xs text-muted-foreground">{t("storefrontDesign.draftPreview")}</span>
				</div>
			)}
			<div className="grid grid-cols-2 gap-2">
				<select
					aria-label={t("storefrontDesign.previewPage")}
					value={page}
					onChange={e => setPage(e.target.value)}
					className="h-11 min-w-0 rounded-full border bg-background px-3 text-sm"
				>
					{["catalog", "contact", "aboutText", "faqText", "termsText"].map(key => (
						<option key={key} value={key}>
							{t(
								key === "catalog" || key === "contact"
									? `storefrontDesign.${key}`
									: key === "termsText"
										? "storefrontDesign.policies"
										: `company.settings.storefront.${key}`,
							)}
						</option>
					))}
				</select>
				<select
					aria-label={t("storefrontDesign.previewSize")}
					value={viewport}
					onChange={e => setViewport(Number(e.target.value))}
					className="h-11 min-w-0 rounded-full border bg-background px-3 text-sm"
				>
					<option value={390}>{t("storefrontDesign.phone")}</option>
					<option value={768}>{t("storefrontDesign.tablet")}</option>
				</select>
			</div>
			{!!catalog?.branches.length && (
				<select
					aria-label={t("storefrontDesign.previewBranch")}
					value={catalog.branchId ?? ""}
					onChange={e => setBranchId(Number(e.target.value))}
					className="h-11 w-full rounded-full border bg-background px-3 text-sm"
				>
					{catalog.branches.map(b => (
						<option key={b.id} value={b.id}>
							{b.name}
						</option>
					))}
				</select>
			)}
			{(catalogError || frameError) && (
				<div role="alert" className="rounded-xl border p-3 text-sm">
					<p>{t("storefrontDesign.previewError")}</p>
					<Button variant="outline" onClick={retry}>
						{t("common.retry")}
					</Button>
				</div>
			)}
			<div
				ref={container}
				dir="ltr"
				className="relative overflow-hidden rounded-2xl border bg-white"
				style={{ height: 760 * scale }}
			>
				{(loading || !ready) && !frameError && !catalogError && (
					<div role="status" className="absolute inset-0 z-10 grid place-items-center bg-background text-sm">
						{t("common.loading")}
					</div>
				)}
				{!loading && !catalogError && (
					<iframe
						key={revision}
						src={url.href}
						ref={frame}
						onLoad={() => frame.current?.contentWindow?.postMessage(payload, url.origin)}
						title={t("storefrontDesign.preview")}
						sandbox="allow-scripts allow-same-origin"
						referrerPolicy="no-referrer"
						className="block origin-top-left border-0"
						style={{
							width: viewport,
							height: 760,
							transform: `scale(${scale})`,
							visibility: catalogError || frameError ? "hidden" : "visible",
						}}
					/>
				)}
			</div>
			<p className="text-center text-xs text-muted-foreground">{t("storefrontDesign.previewOnly")}</p>
		</section>
	);
}
